import axios from 'axios';
import createSignedRequest from './createSignedRequest';
import {
  friendlyConsultantClientError,
  loadPendingContractsFromSession,
  removePendingContractFromSession,
  savePendingContractToSession,
} from '../../components/utils/consultantClientUtils';

const { apiURL } = require('./apiURL');

export const Types = {
  SET_CLIENTS: 'CONSULTANT_CLIENTS_SET',
  SET_RENEWALS: 'CONSULTANT_CLIENTS_RENEWALS',
  SET_SCHEDULES: 'CONSULTANT_CLIENTS_SCHEDULES',
  SET_DIRECT_PRODUCTS: 'CONSULTANT_CLIENTS_DIRECT_PRODUCTS',
  SET_PENDING_CONTRACTS: 'CONSULTANT_CLIENTS_PENDING_CONTRACTS',
  LOADING: 'CONSULTANT_CLIENTS_LOADING',
  ERROR: 'CONSULTANT_CLIENTS_ERROR',
  LAST_CREATE: 'CONSULTANT_CLIENTS_LAST_CREATE',
};

const setLoading = (key, value) => ({ type: Types.LOADING, key, value });
const setError = (error) => ({ type: Types.ERROR, error });

const orgHeader = (state) => ({
  Organisation: String(state.currentOrganisation || state.profile?.details?.consultantAdmin || ''),
});

const parseApiError = (e) => {
  const errBody = e?.response?.data || {};
  const code = errBody.error || errBody.code;
  return {
    ok: false,
    error: code || errBody,
    message: friendlyConsultantClientError(errBody),
    status: e?.response?.status,
  };
};

/**
 * Loads consultant client orgs with full lifecycle fields.
 * @param {boolean} [includeAll=true]
 */
export const getConsultantClients = (includeAll = true) => async (dispatch, getState) => {
  dispatch(setLoading('clients', true));
  dispatch(setError(null));
  try {
    const qs = includeAll ? '?includeAll=true' : '';
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/admin/organisations${qs}`, null, orgHeader(getState())),
    );
    const data = response.data || {};
    const clients = Array.isArray(data) ? data : data.organisations || data.items || [];
    dispatch({ type: Types.SET_CLIENTS, payload: clients });
    return { ok: true, data: clients };
  } catch (e) {
    const result = parseApiError(e);
    dispatch(setError(result.message));
    return result;
  } finally {
    dispatch(setLoading('clients', false));
  }
};

/** @param {object} body — POST /my/consultant/clients body */
export const createConsultantClient = (body) => async (dispatch, getState) => {
  dispatch(setLoading('create', true));
  dispatch(setError(null));
  try {
    const response = await axios(
      await createSignedRequest('POST', `${apiURL}/my/consultant/clients`, JSON.stringify(body), orgHeader(getState())),
    );
    const data = response.data || {};
    dispatch({ type: Types.LAST_CREATE, payload: data });

    const orgId = data.organisation?.id;
    const contractId = data.contract?.id;
    if (orgId && contractId) {
      dispatch({ type: Types.SET_PENDING_CONTRACTS, payload: { [orgId]: contractId } });
      savePendingContractToSession(orgId, contractId);
    }

    await dispatch(getConsultantClients(true));
    await dispatch(getConsultantSchedules());
    return { ok: true, data };
  } catch (e) {
    const result = parseApiError(e);
    dispatch(setError(result.message));
    return result;
  } finally {
    dispatch(setLoading('create', false));
  }
};

/** @param {string} orgId */
export const activateConsultantClient = (orgId) => async (dispatch, getState) => {
  dispatch(setLoading(`activate-${orgId}`, true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/clients/${encodeURIComponent(orgId)}/activate`,
        JSON.stringify({}),
        orgHeader(getState()),
      ),
    );
    await dispatch(getConsultantClients(true));
    await dispatch(getConsultantSchedules());
    return { ok: true, data: response.data };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading(`activate-${orgId}`, false));
  }
};

/** @param {string} orgId @param {object} body */
export const amendConsultantClient = (orgId, body) => async (dispatch, getState) => {
  dispatch(setLoading(`amend-${orgId}`, true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/clients/${encodeURIComponent(orgId)}/amendments`,
        JSON.stringify(body),
        orgHeader(getState()),
      ),
    );
    const data = response.data || {};
    const contractId = data.contract?.id;
    if (contractId) {
      dispatch({ type: Types.SET_PENDING_CONTRACTS, payload: { [orgId]: contractId } });
      savePendingContractToSession(orgId, contractId);
    }
    await dispatch(getConsultantClients(true));
    await dispatch(getConsultantSchedules());
    return { ok: true, data };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading(`amend-${orgId}`, false));
  }
};

export const getConsultantRenewals = () => async (dispatch, getState) => {
  dispatch(setLoading('renewals', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/consultant/clients/renewals`, null, orgHeader(getState())),
    );
    const renewals = response.data?.renewals || [];
    dispatch({ type: Types.SET_RENEWALS, payload: renewals });
    return { ok: true, data: renewals };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading('renewals', false));
  }
};

/** All end-client schedule rows on consultant master contract. */
export const getConsultantSchedules = () => async (dispatch, getState) => {
  dispatch(setLoading('schedules', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/consultant/clients/schedules`, null, orgHeader(getState())),
    );
    const payload = {
      schedules: response.data?.schedules || [],
      masterPerReportFeeVal: response.data?.masterPerReportFeeVal ?? null,
    };
    dispatch({ type: Types.SET_SCHEDULES, payload });
    return { ok: true, data: payload };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading('schedules', false));
  }
};

/** @param {object} body — POST /my/consultant/clients/onboard-existing */
export const onboardExistingConsultantClient = (body) => async (dispatch, getState) => {
  dispatch(setLoading('onboardExisting', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/clients/onboard-existing`,
        JSON.stringify(body),
        orgHeader(getState()),
      ),
    );
    await dispatch(getConsultantClients(true));
    await dispatch(getConsultantSchedules());
    return { ok: true, data: response.data };
  } catch (e) {
    const result = parseApiError(e);
    dispatch(setError(result.message));
    return result;
  } finally {
    dispatch(setLoading('onboardExisting', false));
  }
};

/** @param {string} orgId — GET /admin/consultant-clients/:orgId/schedule */
export const getAdminConsultantClientSchedule = (orgId) => async () => {
  try {
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/admin/consultant-clients/${encodeURIComponent(orgId)}/schedule`, null),
    );
    return { ok: true, data: response.data };
  } catch (e) {
    return parseApiError(e);
  }
};

/** @param {string} orgId @param {object} body — PATCH /admin/consultant-clients/:orgId/schedule */
export const patchAdminConsultantClientSchedule = (orgId, body) => async () => {
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/admin/consultant-clients/${encodeURIComponent(orgId)}/schedule`,
        JSON.stringify(body),
      ),
    );
    return { ok: true, data: response.data };
  } catch (e) {
    return parseApiError(e);
  }
};

/** @param {string} currency — ISO lower-case e.g. gbp */
export const getDirectSubscriptionProducts = (currency = 'gbp') => async (dispatch, getState) => {
  dispatch(setLoading('directProducts', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'GET',
        `${apiURL}/my/consultant/clients/direct-subscription-products?currency=${encodeURIComponent(currency)}`,
        null,
        orgHeader(getState()),
      ),
    );
    const products = response.data?.products || [];
    dispatch({ type: Types.SET_DIRECT_PRODUCTS, payload: { currency, products } });
    return { ok: true, data: products };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading('directProducts', false));
  }
};

/** @param {string} contractId @param {{ rotateToken?: boolean }} [body] */
export const resendConsultantClientContract = (contractId, body = { rotateToken: true }) => async (dispatch, getState) => {
  dispatch(setLoading(`resend-${contractId}`, true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/clients/${encodeURIComponent(contractId)}/resend`,
        JSON.stringify(body),
        orgHeader(getState()),
      ),
    );
    return { ok: true, data: response.data };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading(`resend-${contractId}`, false));
  }
};

/** @param {string} contractId @param {{ reason?: string }} body */
export const voidConsultantClientContract = (contractId, body) => async (dispatch, getState) => {
  dispatch(setLoading(`void-${contractId}`, true));
  try {
    await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/clients/${encodeURIComponent(contractId)}/void`,
        JSON.stringify(body),
        orgHeader(getState()),
      ),
    );
    return { ok: true };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading(`void-${contractId}`, false));
  }
};

export const hydratePendingContracts = () => (dispatch) => {
  dispatch({ type: Types.SET_PENDING_CONTRACTS, payload: loadPendingContractsFromSession() });
};

export const clearPendingContract = (orgId) => (dispatch) => {
  removePendingContractFromSession(orgId);
  dispatch({ type: Types.SET_PENDING_CONTRACTS, payload: { [orgId]: null }, remove: true });
};

export const Actions = {
  getConsultantClients,
  createConsultantClient,
  activateConsultantClient,
  amendConsultantClient,
  getConsultantRenewals,
  getConsultantSchedules,
  onboardExistingConsultantClient,
  getAdminConsultantClientSchedule,
  patchAdminConsultantClientSchedule,
  getDirectSubscriptionProducts,
  resendConsultantClientContract,
  voidConsultantClientContract,
  hydratePendingContracts,
  clearPendingContract,
};

export default Actions;
